import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { adsApi } from '@/api/client';
import { formatCurrencyValue } from '@/api/keywordMetrics';
import { EditableTargetBidCell } from '@/components/EditableTargetBidCell';
import { TableSearchInput } from '@/components/TableSearchInput';
import { filterRowsByTableSearch } from '@/utils/tableSearch';
import { resolveEditableSpTargetBid } from '@/api/targetBidUpdate';
import {
  formatTargetExpression,
  isProductTargetingClause,
  parseSpTargetsPayload,
  targetExpressionsDiffer,
  type SpTargetRow,
} from '@/api/spTargets';

type TargetStateFilter = 'ALL' | 'ENABLED' | 'PAUSED' | 'ARCHIVED';

type CampaignProductTargetsSectionProps = {
  profileId: string;
  campaignId: string;
  adGroupNameById?: Map<string, string>;
  /** Scope the target list to a single ad group (also shows auto-targeting clauses). */
  adGroupId?: string;
  /** Render without the outer card (when nested inside another panel). */
  embedded?: boolean;
};

function formatBid(bid: number | undefined): string {
  if (bid == null) return '-';
  return formatCurrencyValue(bid);
}

export function CampaignProductTargetsSection({
  profileId,
  campaignId,
  adGroupNameById,
  adGroupId,
  embedded = false,
}: CampaignProductTargetsSectionProps) {
  const [stateFilter, setStateFilter] = useState<TargetStateFilter>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [accumulatedTargets, setAccumulatedTargets] = useState<SpTargetRow[]>([]);

  const statesParam =
    stateFilter === 'ALL' ? (['ENABLED', 'PAUSED', 'ARCHIVED'] as const) : ([stateFilter] as const);

  const {
    data: targetsData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['campaign-sp-targets', profileId, campaignId, stateFilter, nextToken, adGroupId ?? null],
    queryFn: async () => {
      const res = await adsApi.listSpTargets(profileId, {
        campaign_ids: [campaignId],
        ...(adGroupId ? { ad_group_ids: [adGroupId] } : {}),
        states: [...statesParam],
        max_results: 500,
        next_token: nextToken,
      });
      if (!res.success) throw new Error('Failed to load product targets');
      return res.data;
    },
    enabled: !!profileId && !!campaignId,
  });

  const targetsPayload = useMemo(() => parseSpTargetsPayload(targetsData), [targetsData]);

  useEffect(() => {
    setNextToken(null);
    setAccumulatedTargets([]);
  }, [profileId, campaignId, stateFilter, adGroupId]);

  useEffect(() => {
    if (!targetsData || isFetching) return;
    const payload = parseSpTargetsPayload(targetsData);
    if (!nextToken) {
      setAccumulatedTargets(payload.targets);
      return;
    }
    setAccumulatedTargets((prev) => [...prev, ...payload.targets]);
  }, [targetsData, nextToken, isFetching]);

  const targets = useMemo(() => {
    const all = accumulatedTargets.length > 0 ? accumulatedTargets : targetsPayload.targets;
    // Inside an ad group, show every clause so auto ad groups list their auto targets.
    return adGroupId ? all : all.filter(isProductTargetingClause);
  }, [accumulatedTargets, targetsPayload.targets, adGroupId]);

  const visibleTargets = useMemo(() => {
    return filterRowsByTableSearch(targets, searchQuery, (target) => {
      const adGroupId = target.ad_group_id || '';
      const adGroupName =
        (adGroupId && adGroupNameById?.get(adGroupId)) || adGroupId || '-';
      const showResolved = targetExpressionsDiffer(
        target.expression,
        target.resolved_expression
      );
      return [
        formatTargetExpression(target.expression),
        showResolved ? formatTargetExpression(target.resolved_expression) : null,
        target.expression_type,
        adGroupName,
        formatBid(target.bid),
        target.state,
        target.target_id,
      ];
    });
  }, [targets, searchQuery, adGroupNameById]);

  const handleStateFilterChange = (value: TargetStateFilter) => {
    setStateFilter(value);
  };

  const handleRefresh = () => {
    setNextToken(null);
    setAccumulatedTargets([]);
    void refetch();
  };

  const handleLoadMore = () => {
    if (!targetsPayload.nextToken) return;
    setNextToken(targetsPayload.nextToken);
  };

  const showLoading = (isLoading || isFetching) && targets.length === 0;

  return (
    <div className={embedded ? undefined : 'bg-surface rounded-xl shadow border border-border p-6'}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Product targeting</h2>
          {!showLoading && !error && (
            <p className="text-sm text-foreground-subtle mt-0.5">{targetsPayload.totalResults} total</p>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <TableSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search product targets…"
            id="campaign-product-targets-search"
          />
          <div>
            <label className="block text-xs font-medium text-foreground-muted uppercase tracking-wide mb-1">
              State
            </label>
            <select
              value={stateFilter}
              onChange={(e) => handleStateFilterChange(e.target.value as TargetStateFilter)}
              className="rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
            >
              <option value="ALL">All</option>
              <option value="ENABLED">Enabled</option>
              <option value="PAUSED">Paused</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            className="text-sm px-3 py-2 border border-border rounded-lg hover:bg-page"
          >
            Refresh
          </button>
        </div>
      </div>

      {showLoading ? (
        <div className="flex items-center gap-2 text-sm text-foreground-subtle">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading product targets...
        </div>
      ) : error ? (
        <div className="text-sm text-error">
          {(error as Error).message || 'Failed to load product targets'}
          <button type="button" onClick={handleRefresh} className="ml-2 text-brand underline">
            Retry
          </button>
        </div>
      ) : targets.length === 0 ? (
        <p className="text-sm text-foreground-subtle">{adGroupId ? 'No targets found for this ad group.' : 'No product targets found for this campaign.'}</p>
      ) : visibleTargets.length === 0 ? (
        <p className="text-sm text-foreground-subtle">No rows match your search.</p>
      ) : (
        <>
          {searchQuery.trim() ? (
            <p className="text-sm text-foreground-subtle mb-2">
              Showing {visibleTargets.length} of {targets.length} product target(s)
            </p>
          ) : null}
          <div className="max-h-64 overflow-x-auto overflow-y-auto rounded-xl border border-border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-page text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
                  <th className="px-3 py-2">{adGroupId ? 'Target' : 'Expression'}</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Ad group</th>
                  <th className="px-3 py-2">Bid</th>
                  <th className="px-3 py-2">State</th>
                  <th className="px-3 py-2">Target ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visibleTargets.map((target, index) => {
                  const targetId = target.target_id || `idx-${index}`;
                  const adGroupId = target.ad_group_id || '';
                  const adGroupName =
                    (adGroupId && adGroupNameById?.get(adGroupId)) || adGroupId || '-';
                  const expressionLabel = formatTargetExpression(target.expression);
                  const showResolved =
                    targetExpressionsDiffer(target.expression, target.resolved_expression);

                  return (
                    <tr key={targetId} className="bg-surface hover:bg-page/80">
                      <td className="px-3 py-2 text-foreground">
                        <div>{expressionLabel}</div>
                        {showResolved ? (
                          <div className="text-xs text-foreground-subtle mt-0.5">
                            Resolved: {formatTargetExpression(target.resolved_expression)}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-foreground-muted">{target.expression_type || '-'}</td>
                      <td className="px-3 py-2 text-foreground-muted" title={adGroupId}>
                        {adGroupName}
                      </td>
                      <td className="px-3 py-2 text-foreground-muted font-mono">
                        <EditableTargetBidCell
                          profileId={profileId}
                          displayValue={formatBid(target.bid)}
                          editableTarget={resolveEditableSpTargetBid(target)}
                          targetLabel={formatTargetExpression(target.expression)}
                          onUpdated={() => void refetch()}
                        />
                      </td>
                      <td className="px-3 py-2 text-foreground-muted">{target.state || '-'}</td>
                      <td className="px-3 py-2 text-foreground-muted font-mono">{target.target_id || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {searchQuery.trim() && targetsPayload.nextToken ? (
            <p className="text-xs text-foreground-subtle mt-2">
              Search applies to loaded rows only — load more to include additional results.
            </p>
          ) : null}
          {targetsPayload.nextToken ? (
            <div className="mt-3">
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={isFetching}
                className="text-sm px-3 py-2 border border-border rounded-lg hover:bg-page disabled:opacity-50"
              >
                {isFetching ? 'Loading…' : 'Load more'}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
